import { normalizeDocName } from "./document-validity";
import { findDriverDocumentFile } from "./driver-documents";

/**
 * @single-source-of-truth Document status computation.
 * All document status derivation in this monorepo must go through getDocumentStatus().
 * Do not add version_status checks in UI components, hooks, or app utils.
 *
 * OUT OF SCOPE (extend here when ready):
 *   - EXPIRED / APPROVED_EXPIRING: validity is derived separately from expiresAt.
 */

export type DocumentUIStatus =
  | "MISSING" // No DB row exists for this document slot
  | "PROCESSING" // Driver uploaded; Lambda hasn't created the DB row yet (tempObjectUrl present)
  | "PENDING_REVIEW" // DB row exists, version_status = PENDING (awaiting admin review)
  | "APPROVED" // version_status = APPROVED
  | "REJECTED" // version_status = REJECTED (awaiting driver re-upload)
  | "REMOVED" // version_status = REMOVED (admin-removed)
  | "SUPERSEDED" // version_status = SUPERSEDED (historical; hidden from main UI)
  | "INCOMPLETE_DATA"; // Row exists but essential fields (key/bucket) are missing or status is unknown

export type DocumentLike = {
  version_status?: string | null;
  key?: string | null;
  bucket?: string | null;
  tempObjectUrl?: string | null;
  url?: string | null;
  rejectionReason?: string | null;
  additionalInfo?: string | null;
};

export type DocumentStatusInput = {
  /**
   * The single document row the caller has pre-selected as canonical for this
   * document slot. Null means no DB row exists in the database.
   *
   * The CALLER is responsible for version selection:
   *   - Driver UI: prefer latest PENDING over latest APPROVED
   *   - Admin UI: use the latest row regardless of status
   */
  dbDoc: {
    version_status: string;
    key: string | null | undefined;
    bucket: string | null | undefined;
    rejectionReason?: string | null;
    additionalInfo?: string | null;
  } | null;

  /**
   * True when the driver completed a presigned PUT (client has tempObjectUrl)
   * but the AWS Lambda creating the DB row is still in flight.
   * Only evaluated when dbDoc is null. Defaults to false.
   */
  hasTempUpload?: boolean;
};

export type DocumentStatusResult = {
  status: DocumentUIStatus;

  /**
   * Whether the document file is accessible for preview/download.
   * - APPROVED                    -> true  (requires a resolved URL)
   * - PENDING_REVIEW              -> true  (admin preview requires a resolved URL)
   * - PROCESSING                  -> true  (client tempObjectUrl)
   * - MISSING / REJECTED / REMOVED / SUPERSEDED / INCOMPLETE_DATA -> false
   */
  hasAccessibleFile: boolean;

  /**
   * Populated only when status === "REJECTED". Null for all other statuses.
   */
  rejection: {
    reason: string | null;
    additionalInfo: string | null;
  } | null;
};

function isDocumentStatusInput(val: unknown): val is DocumentStatusInput {
  return typeof val === "object" && val !== null && "dbDoc" in val;
}

/**
 * Derives canonical document status from raw DB document fields, a StoredMediaFile/Document object,
 * or local upload state.
 */
export function getDocumentStatus(
  inputOrDoc?: DocumentStatusInput | DocumentLike | null,
  options?: { hasTempUpload?: boolean }
): DocumentStatusResult {
  let dbDoc: DocumentStatusInput["dbDoc"] = null;
  let hasTempUpload = options?.hasTempUpload ?? false;

  if (isDocumentStatusInput(inputOrDoc)) {
    dbDoc = inputOrDoc.dbDoc;
    hasTempUpload = inputOrDoc.hasTempUpload ?? false;
  } else if (inputOrDoc) {
    const hasStatus =
      typeof inputOrDoc.version_status === "string" && inputOrDoc.version_status.length > 0;
    const hasKey = typeof inputOrDoc.key === "string" && inputOrDoc.key.trim().length > 0;

    hasTempUpload = options?.hasTempUpload ?? Boolean(inputOrDoc.tempObjectUrl);

    if (hasStatus || hasKey) {
      dbDoc = {
        version_status: inputOrDoc.version_status ?? "",
        key: inputOrDoc.key,
        bucket: inputOrDoc.bucket ?? "default",
        rejectionReason: inputOrDoc.rejectionReason,
        additionalInfo: inputOrDoc.additionalInfo,
      };
    } else {
      dbDoc = null;
    }
  }

  // 1. No database record
  if (!dbDoc) {
    if (hasTempUpload) {
      return {
        status: "PROCESSING",
        hasAccessibleFile: true,
        rejection: null,
      };
    }
    return {
      status: "MISSING",
      hasAccessibleFile: false,
      rejection: null,
    };
  }

  // 2. Integrity check on storage identifiers
  const key = typeof dbDoc.key === "string" ? dbDoc.key.trim() : "";
  const bucket = typeof dbDoc.bucket === "string" ? dbDoc.bucket.trim() : "";
  if (!key || !bucket) {
    return {
      status: "INCOMPLETE_DATA",
      hasAccessibleFile: false,
      rejection: null,
    };
  }

  // 3. Dispatch on version_status
  switch (dbDoc.version_status) {
    case "PENDING":
      return {
        status: "PENDING_REVIEW",
        hasAccessibleFile: true,
        rejection: null,
      };

    case "APPROVED":
      return {
        status: "APPROVED",
        hasAccessibleFile: true,
        rejection: null,
      };

    case "REJECTED":
      return {
        status: "REJECTED",
        hasAccessibleFile: false,
        rejection: {
          reason: dbDoc.rejectionReason ?? null,
          additionalInfo: dbDoc.additionalInfo ?? null,
        },
      };

    case "REMOVED":
      return {
        status: "REMOVED",
        hasAccessibleFile: false,
        rejection: null,
      };

    case "SUPERSEDED":
      return {
        status: "SUPERSEDED",
        hasAccessibleFile: false,
        rejection: null,
      };

    default:
      return {
        status: "INCOMPLETE_DATA",
        hasAccessibleFile: false,
        rejection: null,
      };
  }
}

// ─── Rank / match / select helpers ──────────────────────────────────────────────

/** Priority ranking for picking the most-favorable document version per slot. */
export const DOC_STATUS_RANK: Record<DocumentUIStatus, number> = {
  PENDING_REVIEW: 4,
  PROCESSING: 3,
  APPROVED: 2,
  REJECTED: 1,
  REMOVED: 0,
  SUPERSEDED: 0,
  MISSING: 0,
  INCOMPLETE_DATA: 0,
};

/** Get the canonical status string for a doc (convenience wrapper). */
export function getDocCanonicalStatus(
  doc: DocumentLike,
  hasTempUpload?: boolean
): DocumentUIStatus {
  return getDocumentStatus(doc, { hasTempUpload }).status;
}

/**
 * Check if a document matches a given upload-field name.
 * Matches against the S3 filename (last key segment) and the label, both
 * normalized via `normalizeDocName` (lowercase, strip trailing _?path).
 */
export function docMatchesField(doc: DocumentLike, field: string): boolean {
  const keyName = doc.key?.split("/").pop() ?? "";
  const label = (doc as Record<string, unknown>).label;
  const candidates = [keyName, typeof label === "string" ? label : ""]
    .map(normalizeDocName)
    .filter(Boolean);
  return candidates.includes(normalizeDocName(field));
}

/**
 * Find the best (most-favorable) document version for a given upload field.
 * Prefers PENDING_REVIEW > APPROVED > REJECTED, so a newer pending renewal
 * always wins over an older approved one for the same field.
 */
export function selectBestDocForField<T extends DocumentLike>(docs: T[], field: string): T | null {
  let best: T | null = null;
  for (const doc of docs) {
    if (!docMatchesField(doc, field)) continue;
    if (!best) {
      best = doc;
    } else {
      const docRank = DOC_STATUS_RANK[getDocCanonicalStatus(doc)] ?? 0;
      const bestRank = DOC_STATUS_RANK[getDocCanonicalStatus(best)] ?? 0;
      if (docRank > bestRank) best = doc;
    }
  }
  return best;
}

/**
 * Compare two documents by status rank (higher rank = more favorable).
 * Returns negative if a is better, positive if b is better, 0 if equal.
 */
export function compareDocStatus(a: DocumentLike, b: DocumentLike): number {
  const aRank = DOC_STATUS_RANK[getDocCanonicalStatus(a)] ?? 0;
  const bRank = DOC_STATUS_RANK[getDocCanonicalStatus(b)] ?? 0;
  return bRank - aRank;
}

/**
 * Filter docs that match a field, then return them sorted by status rank
 * (best first). Useful for deduplication loops.
 */
export function filterAndSortDocsForField<T extends DocumentLike>(docs: T[], field: string): T[] {
  return docs.filter((d) => docMatchesField(d, field)).sort(compareDocStatus);
}

// ─── Rejection reasons ────────────────────────────────────────────────────────

/**
 * Canonical rejection-reason keys, shared by admin (i18n select options) and
 * mobile (French label map). Backend stores reasons free-form; keep this list
 * in sync with what reviewers can actually submit.
 */
export const DOCUMENT_REJECTION_REASONS = [
  "blurry_image",
  "incomplete_document",
  "invalid_document",
  "expired_document",
  "mismatched_information",
  "other",
] as const;

export type DocumentRejectionReason = (typeof DOCUMENT_REJECTION_REASONS)[number];

/**
 * French source of truth for rejection reasons. Short labels are used by the
 * mobile app and must match admin `document.rejectReason.*` (fr); the push
 * sentences are the driver-facing notification body. Both are keyed by code.
 */
export const DOCUMENT_REJECTION_REASON_LABELS_FR: Record<DocumentRejectionReason, string> = {
  blurry_image: "Image floue",
  incomplete_document: "Document incomplet",
  invalid_document: "Document invalide",
  expired_document: "Document expiré",
  mismatched_information: "Informations non conformes",
  other: "Autre",
};

export const DOCUMENT_REJECTION_REASON_MESSAGES_FR: Record<DocumentRejectionReason, string> = {
  blurry_image: "L’image de la version envoyée est floue",
  incomplete_document: "La version envoyée est incomplète",
  invalid_document: "La version envoyée n’est pas conforme",
  expired_document: "La version envoyée est expirée",
  mismatched_information: "Les informations de la version envoyée ne correspondent pas",
  other: "Autre motif",
};

export const isDocumentRejectionReason = (
  reason: string | null | undefined
): reason is DocumentRejectionReason =>
  !!reason && (DOCUMENT_REJECTION_REASONS as readonly string[]).includes(reason);

/** Short French label for a reason code; free-form reasons are returned as-is. */
export function getRejectionReasonLabelFr(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return isDocumentRejectionReason(reason) ? DOCUMENT_REJECTION_REASON_LABELS_FR[reason] : reason;
}

// ─── Driver document labels ───────────────────────────────────────────────────

/** French label for a stored document name; unknown names are returned as-is. */
export function getDriverDocumentLabelFr(name: string): string {
  return findDriverDocumentFile(name)?.label ?? name;
}
